// Brief-Vorlagen (dokument_vorlagen, Spec 2026-03-17): Kategorien, Platzhalter `{{gruppe.feld}}`, Vorschau.

export const BRIEF_KATEGORIEN = [
  ['mietvertrag', 'Mietvertrag'], ['kuendigung', 'Kündigung'], ['uebergabeprotokoll', 'Übergabeprotokoll'],
  ['mieterhoehung', 'Mieterhöhung'], ['mahnung', 'Mahnung'], ['nebenkostenbrief', 'NK-Anschreiben'], ['anschreiben', 'Anschreiben'], ['sonstig', 'Sonstig'],
] as const;
export type BriefKategorie = typeof BRIEF_KATEGORIEN[number][0];
export const kategorieLabel = (k: string) => BRIEF_KATEGORIEN.find(([key]) => key === k)?.[1] ?? k;

/** Gruppen-Überschriften der Platzhalter (Spec-Tabelle „Verfügbare Platzhalter-Gruppen“). */
export const PLATZHALTER_GRUPPEN: Record<string, string> = {
  empfaenger: 'Empfänger', mieter: 'Mieter', objekt: 'Objekt', vertrag: 'Vertrag', eigentuemer: 'Eigentümer', datum: 'Allgemein', firma: 'Allgemein',
};

const MUSTER = /\{\{\s*([a-z_]+(?:\.[a-z_]+)+)\s*\}\}/gi;

/** Alle Platzhalter im HTML, in Reihenfolge des ersten Auftretens. */
export function findePlatzhalter(html: string): string[] {
  const gesehen: string[] = [];
  for (const m of html.matchAll(MUSTER)) { const k = m[1].toLowerCase(); if (!gesehen.includes(k)) gesehen.push(k); }
  return gesehen;
}

export interface PlatzhalterInfo { key: string; label: string }

/** Nach Gruppe sortiert; Labels aus der Spalte `platzhalter`, sonst der Feldname. */
export function gruppierePlatzhalter(keys: readonly string[], labels: readonly PlatzhalterInfo[] = []): { gruppe: string; eintraege: PlatzhalterInfo[] }[] {
  const nachLabel = new Map(labels.map(l => [l.key.toLowerCase(), l.label]));
  const gruppen = new Map<string, PlatzhalterInfo[]>();
  for (const key of keys) {
    const g = PLATZHALTER_GRUPPEN[key.split('.')[0]] ?? 'Weitere';
    const liste = gruppen.get(g) ?? [];
    liste.push({ key, label: nachLabel.get(key) ?? key.split('.').slice(1).join('.') });
    gruppen.set(g, liste);
  }
  return [...gruppen.entries()].map(([gruppe, eintraege]) => ({ gruppe, eintraege }));
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Vorschau-HTML: Platzhalter grün markiert – mit Beispielwerten (sofern vorhanden) oder als `{{key}}`.
 * Werte werden escaped; das Ergebnis läuft ohnehin nur in einem sandboxed iframe.
 */
export function vorschauHtml(html: string, werte: Readonly<Record<string, string>> | null): string {
  return html.replace(MUSTER, (_, roh: string) => {
    const key = roh.toLowerCase();
    const wert = werte?.[key];
    const inhalt = wert !== undefined ? esc(wert) : `{{${esc(key)}}}`;
    return `<mark style="background:rgba(42,143,92,0.16);color:#1f6b45;border-radius:3px;padding:0 2px">${inhalt}</mark>`;
  });
}

/** Neutrale Beispieldaten für die Vorschau („Mit Beispieldaten anzeigen“) – erkennbar keine echten Daten. */
export function beispielWerte(heute: Date): Record<string, string> {
  const kurz = heute.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const lang = heute.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
  return {
    'empfaenger.briefanrede': 'Sehr geehrter Herr Mustermann,', 'empfaenger.name': 'Max Mustermann', 'empfaenger.vorname': 'Max', 'empfaenger.nachname': 'Mustermann', 'empfaenger.firma': '',
    'mieter.anrede': 'Herr', 'mieter.vorname': 'Max', 'mieter.nachname': 'Mustermann', 'mieter.email': 'max.mustermann@beispiel.de',
    'mieter.telefon': '0123 456789', 'mieter.geburtsdatum': '01.01.1980',
    'objekt.bezeichnung': 'Musterhaus', 'objekt.strasse': 'Musterstraße', 'objekt.hausnummer': '1', 'objekt.plz': '12345', 'objekt.ort': 'Musterstadt',
    'vertrag.vertragsnummer': 'MV-0000', 'vertrag.beginn': '01.01.2026', 'vertrag.ende': 'unbefristet', 'vertrag.kaltmiete': '750,00 €',
    'vertrag.nebenkosten': '180,00 €', 'vertrag.kaution': '2.250,00 €', 'vertrag.kuendigungsfrist': '3 Monate',
    'eigentuemer.vorname': 'Erika', 'eigentuemer.nachname': 'Musterfrau', 'eigentuemer.firma_name': 'Muster GmbH',
    'datum.heute': kurz, 'datum.heute_lang': lang,
  };
}

/** Platzhalter, die beim Brief gefüllt werden (briefDaten.ts briefWerte + empfaengerWerte) – Auswahl im Vorlagen-Editor. */
export const VERFUEGBARE_PLATZHALTER: { gruppe: string; eintraege: PlatzhalterInfo[] }[] = [
  { gruppe: 'Empfänger', eintraege: [
    { key: 'empfaenger.briefanrede', label: 'Briefanrede („Sehr geehrte Frau …,“)' }, { key: 'empfaenger.name', label: 'Name' },
    { key: 'empfaenger.vorname', label: 'Vorname' }, { key: 'empfaenger.nachname', label: 'Nachname' }, { key: 'empfaenger.firma', label: 'Firma' },
  ] },
  { gruppe: 'Objekt', eintraege: [
    { key: 'objekt.nummer', label: 'Objektnummer' }, { key: 'objekt.bezeichnung', label: 'Bezeichnung' }, { key: 'objekt.anschrift', label: 'Anschrift (Straße, PLZ Ort)' },
    { key: 'objekt.strasse', label: 'Straße' }, { key: 'objekt.hausnummer', label: 'Hausnummer' }, { key: 'objekt.plz', label: 'PLZ' }, { key: 'objekt.ort', label: 'Ort' },
    { key: 'objekt.verwaltungsart', label: 'Verwaltungsart' }, { key: 'objekt.objekttyp', label: 'Gebäudeart' }, { key: 'objekt.baujahr', label: 'Baujahr' },
    { key: 'objekt.heizungsart', label: 'Heizungsart' }, { key: 'objekt.wohnflaeche', label: 'Wohnfläche' }, { key: 'objekt.energieklasse', label: 'Energieklasse' },
  ] },
  { gruppe: 'Wohnung', eintraege: [
    { key: 'mieter.name', label: 'Name des Mieters' }, { key: 'mieter.personen', label: 'Alle Personen der Wohnung' },
    { key: 'einheit.bezeichnung', label: 'Wohnung (Bezeichnung)' }, { key: 'einheit.lage', label: 'Lage der Wohnung (Etage)' },
    { key: 'einheit.wohnflaeche', label: 'Wohnfläche' }, { key: 'einheit.zimmer', label: 'Zimmer' }, { key: 'einheit.badezimmer', label: 'Badezimmer' },
    { key: 'einheit.typ', label: 'Art der Einheit' },
  ] },
  { gruppe: 'Mieter / Vertrag', eintraege: [
    { key: 'vertrag.vertragsnummer', label: 'Kundennummer' }, { key: 'vertrag.beginn', label: 'Mietbeginn' }, { key: 'vertrag.ende', label: 'Mietende' },
    { key: 'vertrag.kaltmiete', label: 'Kaltmiete' }, { key: 'vertrag.nebenkosten', label: 'NK-Vorauszahlung' }, { key: 'vertrag.heizkosten', label: 'Heizkosten-Vorauszahlung' },
    { key: 'vertrag.weitere_kosten', label: 'Weitere Kosten' }, { key: 'vertrag.gesamtmiete', label: 'Gesamtmiete (monatliche Zahlung)' },
    { key: 'vertrag.kaution', label: 'Kaution' }, { key: 'vertrag.zahlungsweise', label: 'Zahlweise' },
    { key: 'mieter.email', label: 'E-Mail' }, { key: 'mieter.telefon', label: 'Telefon' }, { key: 'mieter.geburtsdatum', label: 'Geburtsdatum' },
    { key: 'mieter.iban', label: 'IBAN' }, { key: 'mieter.kontoinhaber', label: 'Kontoinhaber' },
    { key: 'mieter.sepa_mandatsreferenz', label: 'SEPA-Mandatsreferenz' }, { key: 'mieter.sepa_mandatsdatum', label: 'SEPA-Mandat vom' },
    { key: 'mieter.anzahl_bewohner', label: 'Anzahl Bewohner' },
  ] },
  { gruppe: 'Eigentümer des Objekts', eintraege: [
    { key: 'eigentuemer.name', label: 'Name' }, { key: 'eigentuemer.anschrift', label: 'Anschrift' }, { key: 'eigentuemer.name_anschrift', label: 'Name und Anschrift' },
    { key: 'eigentuemer.briefanrede', label: 'Briefanrede' }, { key: 'eigentuemer.anrede', label: 'Anrede' },
    { key: 'eigentuemer.vorname', label: 'Vorname' }, { key: 'eigentuemer.nachname', label: 'Nachname' }, { key: 'eigentuemer.firma_name', label: 'Firma' },
    { key: 'eigentuemer.email', label: 'E-Mail' }, { key: 'eigentuemer.telefon', label: 'Telefon' }, { key: 'eigentuemer.kundennummer', label: 'Kundennummer' },
    { key: 'eigentuemer.iban', label: 'IBAN' }, { key: 'eigentuemer.bic', label: 'BIC' }, { key: 'eigentuemer.kontoinhaber', label: 'Kontoinhaber' },
    { key: 'eigentuemer.glaeubiger_id', label: 'SEPA-Gläubiger-ID' }, { key: 'eigentuemer.sepa_mandatsreferenz', label: 'SEPA-Mandatsreferenz' },
    { key: 'eigentuemer.vertrag_beginn', label: 'Verwaltervertrag seit' }, { key: 'eigentuemer.vertrag_ende', label: 'Verwaltervertrag bis' },
    { key: 'eigentuemer.einheiten', label: 'Einheiten im Objekt (WEG)' }, { key: 'eigentuemer.mea', label: 'MEA' },
    { key: 'eigentuemer.hausgeld', label: 'Hausgeld monatlich (inkl. Rücklage)' }, { key: 'eigentuemer.ruecklage', label: 'Rücklage monatlich' },
    { key: 'eigentuemer.eigentum_seit', label: 'Eigentümer seit' },
  ] },
  { gruppe: 'Allgemein', eintraege: [{ key: 'datum.heute', label: 'Datum (30.10.2026)' }, { key: 'datum.heute_lang', label: 'Datum (30. Oktober 2026)' }] },
];

/** „Eigentümer des Objekts · Anschrift“ je Schlüssel – für Auswahllisten und fehlende Werte (#592) */
export const PLATZHALTER_LABEL: ReadonlyMap<string, string> = new Map(
  VERFUEGBARE_PLATZHALTER.flatMap((g) => g.eintraege.map((p) => [p.key, `${g.gruppe} · ${p.label}`] as [string, string])));

const escText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Einfache Vorlage (Betreff + Text) → HTML für dokument_vorlagen: <h2>Betreff</h2>, Absätze an Leerzeilen. */
export function briefVorlageHtml(betreff: string, text: string): string {
  const absaetze = text.replace(/\r\n/g, '\n').split(/\n{2,}/).map((a) => a.trim()).filter(Boolean)
    .map((a) => `<p>${escText(a).replace(/\n/g, '<br>')}</p>`);
  return `<h2>${escText(betreff.trim())}</h2>\n${absaetze.join('\n')}`;
}

/** Nur schlichte Vorlagen (ohne Tabellen/Bilder) lassen sich im Text-Editor bearbeiten, ohne Formatierung zu verlieren. */
export const istSchlichteVorlage = (html: string) => !/<(table|img|style|div[^>]+style)/i.test(html);
